import {FormikInput} from '~/src/design-system-shims'
import {useNavigate} from 'react-router-dom'
import {Formik, Form as FormikForm} from 'formik'
import debounce from 'lodash/debounce'
import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'
import DowntownBg from '~/src/assets/images/downtown-bg.jpeg'
import DowntownPa from '~/src/assets/images/Paris.jpg'
import {ReusuableHeaderText} from '~/src/components/shared/styles/SharedStyles'
import {useLazyGetUserByEmailQuery} from '~/src/store/apis/user-api'
import AddressAutoComplete from '../../components/platform/onboarding/AddressAutoComplete'
import createAddressItem from '../../components/platform/onboarding/helpers/createAddress'
import type {IAddress, ICompanyBase, ICompensationCode} from '../../models'
import type {IBaseAddress} from '../../models/baseAddress'
import {BASE_ROUTES} from '../../routes'
import {useCreateCompanyMutation} from '../../store/apis/company-api'
import {useAppDispatch} from '../../store/store-hooks'
import CompanyAddressSignInList from '../custom-signin-address-fields/CompanyAddressSignInList'
import {Button} from '~/src/design-system/Primitives'

export type TActivePlatform = 'Company' | 'Agency'

const Outer = styled.div`
  width: 100%;
  height: 100vh;
  flex: 1;
  overflow: hidden;
`
const Background = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  background: linear-gradient(rgba(35, 35, 35, 0.64), rgba(35, 35, 35, 0.64)),
    url(${DowntownBg}) center 40% no-repeat;
  background-size: 135%;
`

const BackgroundPa = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  overflow: hidden;
  background: linear-gradient(rgba(35, 35, 35, 0.64), rgba(35, 35, 35, 0.64)),
    url(${DowntownPa}) center 40% no-repeat;
  background-size: 135%;
`

const MaxWidth = styled.div`
  overflow-y: auto;
  left: 50%;
  max-width: 90%;
  max-height: 90%;
  padding: 30px 9.866%;
  position: relative;
  top: 50%;
  transform: translate(-50%, -50%);

  @media (min-width: 1200px) {
    padding: 0;
  }
`

const Header = styled.div`
  width: 100%;
  border-bottom: 1px solid #cdced9;
`

const HeaderText = styled.div`
  font-size: 28px;
  margin: 25px;
  margin-left: 50px;
  line-height: 32px;
  font-weight: 700;
`

const Red = styled.span`
  color: #f44336; /* Material UI error color */
`

/** DS-layout: use Stack/Grid from ~/src/design-system for new layout code */
const FormRow = styled.div`
  display: grid;
  grid-template-columns: 5fr 1fr 6fr;
  gap: var(--space-6);
`

const EqualColRow = styled.div`
  display: grid;
  gap: var(--space-6);
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`

const AdminRow = styled.div`
  display: grid;
  gap: var(--space-6);
  grid-template-columns: 1fr 1fr 0.5fr 3fr;
`

export interface IFormValues {
  addresses: IAddress[]
  noEmployees: number
  billingAddress: IBaseAddress
  adminEmail: string
  adminFirstName: string
  adminLastName: string
  name: string
  compensationCodes?: ICompensationCode[]
  phoneNumber: string
  website: string
  agencyId?: string
}

interface IProps {
  activePlatform: TActivePlatform
}

export default function CreateCompany({activePlatform}: IProps) {
  const [addCompany, {error}] = useCreateCompanyMutation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const initialValues: IFormValues = {
    name: '',
    noEmployees: 1,
    phoneNumber: '',
    website: '',
    addresses: [createAddressItem()],
    billingAddress: {
      line1: '',
      city: '',
      state: '',
      zipCode: '',
      display: '',
    },
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    agencyId: '',
  }

  const handleSubmit = async (values: IFormValues) => {
    const castValues = values as unknown as Partial<ICompanyBase>
    await addCompany({
      ...castValues,
      agencyId: castValues?.agencyId?.length > 0 ? castValues.agencyId : undefined,
      departments: [],
      noEmployees: Number(values.noEmployees),
    }).unwrap()
    navigate(BASE_ROUTES.REGISTRATION_SUCCESS)
  }

  const [findEmail] = useLazyGetUserByEmailQuery()

  const validateEmail = useCallback(
    async function (value: string | undefined, resolve: (value: boolean) => void) {
      try {
        const alreadyExists = await findEmail(value ?? '').unwrap()
        resolve(!alreadyExists)
      } catch (e: unknown) {
        resolve(true)
      }
    },
    [findEmail],
  )

  const debouncedValidateEmail = useMemo(
    function () {
      return debounce(validateEmail, 500)
    },
    [validateEmail],
  )

  return (
    <Outer>
      {import.meta.env.VITE_FR ? <BackgroundPa /> : <Background />}
      <MaxWidth>
        <>
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'var(--white)',
              border: '1px solid #CDCED9',
              borderRadius: 'var(--radius-sm)',
            }}>
            <Header>
              <HeaderText>Registration</HeaderText>
            </Header>

            <Formik
              onSubmit={handleSubmit}
              initialValues={initialValues}
              validationSchema={Yup.object({
                name: Yup.string().required('Required'),
                phoneNumber: Yup.string()
                  .required('Required')
                  .phone('US', true, 'Phone number is invalid.'),
                adminEmail: Yup.string()
                  .email('Email address is invalid.')
                  .test(
                    'already-exists',
                    'This email is already in use',
                    (value) => new Promise((resolve) => debouncedValidateEmail(value, resolve)),
                  )
                  .required('Required'),
                adminFirstName: Yup.string().required('Required'),
                adminLastName: Yup.string().required('Required'),
                website: Yup.string(),
                addresses: Yup.array().of(
                  Yup.object({
                    id: Yup.string(),
                    name: Yup.string().required('Required'),
                    isOnsite: Yup.boolean().nullable().required('Required'),
                    address: Yup.object({
                      line1: Yup.string(),
                      city: Yup.string(),
                      state: Yup.string(),
                      zipCode: Yup.string(),
                      display: Yup.string().required('Required'),
                      location: Yup.object({
                        type: Yup.string(),
                        coordinates: Yup.array().of(Yup.number()),
                      }).required('Required'),
                    }),
                  }),
                ),
                billingAddress: Yup.object({
                  line1: Yup.string(),
                  city: Yup.string(),
                  state: Yup.string(),
                  zipCode: Yup.string(),
                  display: Yup.string().required('Required'),
                }),
                agencyId: Yup.string().nullable(),
              })}>
              {({isSubmitting, setFieldValue, values, handleChange, errors}) => {
                return (
                  <FormikForm>
                    <div style={{margin: 50}}>
                      <ReusuableHeaderText>Company Information</ReusuableHeaderText>

                      <FormRow>
                        <div>
                          <div style={{paddingBottom: 'var(--space-2)'}}>
                            Company Name<Red>*</Red>:
                          </div>
                          <FormikInput
                            name="name"
                            placeholder="Default Company"
                            inputProps={undefined}
                          />
                        </div>
                        <div />
                        <EqualColRow>
                          <div>
                            <div style={{paddingBottom: 'var(--space-2)'}}>
                              Company Phone<Red>*</Red>:
                            </div>
                            <FormikInput
                              name="phoneNumber"
                              placeholder="1 123 456 7890"
                              inputProps={undefined}
                            />
                          </div>
                          <div>
                            <div style={{paddingBottom: 'var(--space-2)'}}>Company Website:</div>
                            <FormikInput
                              name="website"
                              placeholder="company.com"
                              inputProps={undefined}
                            />
                          </div>
                        </EqualColRow>
                      </FormRow>
                      <div style={{paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-6)'}}>
                        <hr
                          style={{
                            border: 'none',
                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                            margin: 0,
                          }}
                        />
                      </div>

                      {/* Section2 */}

                      <CompanyAddressSignInList
                        values={values}
                        setFieldValue={setFieldValue}
                        activePlatform={activePlatform}
                      />
                      <div style={{paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-6)'}}>
                        <hr
                          style={{
                            border: 'none',
                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                            margin: 0,
                          }}
                        />
                      </div>

                      {/* Section3 */}

                      <div>
                        <div>
                          <div style={{paddingBottom: 'var(--space-2)'}}>
                            Billing Address<Red>*</Red>:
                          </div>
                          <AddressAutoComplete
                            address={values.billingAddress}
                            setFieldValue={setFieldValue}
                            activePlatform={activePlatform}
                            name="billingAddress"
                          />
                        </div>
                      </div>
                      <div style={{paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-6)'}}>
                        <hr
                          style={{
                            border: 'none',
                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                            margin: 0,
                          }}
                        />
                      </div>
                      <AdminRow>
                        <div>
                          <div style={{paddingBottom: 'var(--space-2)'}}>
                            Admin First Name<Red>*</Red>:
                          </div>
                          <FormikInput
                            name="adminFirstName"
                            placeholder="Jill "
                            inputProps={undefined}
                          />
                        </div>
                        <div>
                          <div style={{paddingBottom: 'var(--space-2)'}}>
                            Admin Last Name<Red>*</Red>:
                          </div>
                          <FormikInput
                            name="adminLastName"
                            placeholder="Doe"
                            inputProps={undefined}
                          />
                        </div>
                        <div />
                        <div>
                          <div style={{paddingBottom: 'var(--space-2)'}}>
                            Admin Email<Red>*</Red>:
                          </div>
                          <FormikInput
                            name="adminEmail"
                            placeholder="adminEmail@gmail.com"
                            inputProps={undefined}
                          />
                        </div>
                      </AdminRow>
                      <div style={{color: 'grey', fontWeight: '700', marginTop: 25}}>
                        * The MSP will be provided by Jobsource after registration *
                      </div>

                      <div style={{marginTop: 'var(--space-12)', display: 'flex'}}>
                        <div>
                          <Button
                            onClick={() => {
                              navigate(BASE_ROUTES.SIGNIN)
                            }}
                            variant="secondary"
                            color="secondary"
                            disabled={isSubmitting}>
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            sx={{ml: 2}}>
                            Finish
                          </Button>
                        </div>
                      </div>
                    </div>
                  </FormikForm>
                )
              }}
            </Formik>
          </div>
        </>
      </MaxWidth>
    </Outer>
  )
}
